/*
 * jv-bridal-hub-resilience.js
 * ...........................................................................
 * Client-side write-resilience layer for the Bride Hub. Loaded by
 * sections/jv-bridal-hub.liquid (blocking, before the inline write scripts and
 * the jv-bride-spotlight snippet run).
 *
 * Why this exists: reads are metafield-cached (~200ms); writes go to GAS and
 * take ~8-14s, and they were SILENT when they stalled (button stuck on
 * "Saving..." forever). An anxious bride's worst moment is a save that hangs
 * with no feedback. This adds a hard 15s timeout to every write and a single
 * warm, on-brand error+retry renderer so a stalled/failed save always resolves
 * to a reassuring "try again" within the timeout.
 *
 * CONTRACT (see CLAUDE.md "Bridal Hub direct GAS calls" + "CORS Architecture"):
 *  - Uses the captured native fetch (window._jvNativeFetch) so third-party
 *    Shopify apps that monkey-patch fetch cannot promote the request to a
 *    non-simple CORS preflight that GAS can't serve.
 *  - Content-Type stays text/plain, no custom headers, credentials omitted.
 *  - Every call must carry email + customerId (portalForms_unifiedAuth_ fails
 *    silently otherwise). Callers pass these through opts/JV_HUB_CTX.
 *
 * Writes stay on GAS by design (CLAUDE.md GAS perf wall). This is purely the
 * client resilience layer, not a data-path change.
 */
(function () {
  'use strict';

  window.jvHub = window.jvHub || {};

  // Default GAS web app /exec, the same stable deployment every bridge call uses.
  var DEFAULT_GAS_URL =
    'https://script.google.com/macros/s/AKfycbyoZjG8AN0-0AgURVVoGiXsJSyhV8HSTBZ6MGvd2P3HVXa_Xaf6-PEO848tSUIblOtS/exec';

  /* =====================================================================
   * ROUTE -> BACKEND MAP  (the Cloudflare migration seam)
   * ---------------------------------------------------------------------
   * THIS IS THE ONE PLACE A ROUTE'S BACKEND CHANGES. Moving a hub route
   * off GAS onto a Worker is a ONE-LINE edit to JV_HUB_ROUTES below. No
   * caller changes, no other file changes.
   *
   * Why it exists: the stable GAS /exec URL was hardcoded in 11 places
   * across the hub, so "point this route at the Worker" meant hunting
   * every copy and getting one wrong silently. Now every hub call resolves
   * its URL through jvHub.resolveUrl(route).
   *
   * ADDING A THIRD BACKEND IS A DATA ENTRY, NOT A CODE CHANGE:
   *   1. add it to JV_HUB_BACKENDS      -> d1: 'https://.../api'
   *   2. point routes at it             -> 'bridge_bride_getRunsheet': 'd1'
   * That is the whole change.
   *
   * RESOLUTION ORDER (first match wins):
   *   1. an explicit opts.url passed by the caller   (escape hatch, always wins)
   *   2. JV_HUB_ROUTES[route]                        (per-route override)
   *   3. JV_HUB_ROUTE_PREFIXES, longest prefix first (whole families at once)
   *   4. JV_HUB_BACKENDS[DEFAULT_BACKEND]            (today: gas)
   *
   * A backend value is either a KEY into JV_HUB_BACKENDS ('gas', 'relay')
   * or a literal URL. Unknown keys fall back to the default backend rather
   * than producing an undefined URL -- a typo must never silently post to
   * "undefined" and look like a network failure.
   *
   * NOTE ON THE RELAY: 'relay' is DEFINED here but nothing is pointed at it
   * yet. Contrary to the standing assumption, the majority of hub traffic
   * has always gone DIRECT to GAS, not through the Cloudflare relay. This
   * map does not change where a single byte goes today -- it is the seam
   * that makes changing it a one-line decision. Flip routes deliberately,
   * one at a time, with a parity check, per the migration doctrine.
   * ===================================================================== */

  var JV_HUB_BACKENDS = {
    // The stable Apps Script deployment. Today's destination for everything.
    gas: DEFAULT_GAS_URL,
    // The live Cloudflare CORS relay that proxies through to the same /exec
    // and injects the team key server-side. Defined, intentionally unused.
    relay: 'https://broken-truth-efdd.calm-hill-fd5b.workers.dev'
  };

  var DEFAULT_BACKEND = 'gas';

  // Per-route overrides. EMPTY BY DESIGN -- every route resolves to the
  // default today. This is the line you edit to migrate a route:
  //   'bridge_bride_getPartyMembers': 'relay',
  // ARMED 2026-08-08, Phase 2a. Inspiration-photo uploads now go to a dedicated
  // Cloudflare Worker that stores the photo in R2 and confirms immediately,
  // instead of the bride waiting on the Apps Script cold start.
  //
  // Why: measured 2026-08-08, a GAS route that does NOTHING took 13-22s, and
  // real uploads took 57-59s and were failing live. The same upload through the
  // Worker measured 0.65-0.73s for a 1.9 MB photo.
  //
  // Drive is NOT bypassed. The Worker hands the photo to GAS in the background,
  // so it still lands in the bride's Drive folder permanently (Jess's D1
  // decision). Drive is just no longer in front of the bride.
  //
  // TO ROLL BACK: set this to {} and push. One line, no other change needed.
  //
  // REVERTED 2026-08-21 (same day, follow-up). Briefly held two entries here
  // for jv-bridal-hub-trial-modal.js's two relay calls (BRIEF-04A follow-up).
  // That file turned out to be dead code -- its <script> tag and DOM markup
  // were removed from sections/jv-bridal-hub.liquid on 2026-06-11 (replaced by
  // a direct link to /pages/bridal-trial), then the file itself was correctly
  // deleted as dead code on 2026-08-06, then ACCIDENTALLY resurrected by a
  // later "reconcile git to live" pass that restored it without checking why
  // it was still present on the live theme (the delete had only partially
  // landed remotely). Deleting the file again 2026-08-21; these entries would
  // have had zero callers, so removed rather than left as inert clutter. The
  // real gap it exposed (no self-service trial-record correction path for an
  // opted-out/already-booked bride -- jv-bridal-trial.liquid's only answer is
  // "please email us") is written up as a separate build brief, not silently
  // dropped. See BRIEFS/BUILD-trial-self-correction-cloudflare-2026-08-21.md.
  // ARMED PERMANENTLY 2026-09-03 (BRIEFS/HANDOFF-inspo-upload-worker-rearm-
  // readiness-to-cloudflare-2026-09-03.md + BRIEFS/REPLY-inspo-upload-worker-
  // rearm-readiness-2026-09-03.md). Re-armed only after: (1) the real,
  // independently-verified gallery-collapse bug that blocked the LAST arming
  // attempt was root-caused and fixed (78506918), and that fix was itself
  // live-verified clean under the exact race sequence that broke it twice
  // before (BRIEFS/RESULTS-gallery-shrink-guard-verify-2026-09-03.md); (2) a
  // fresh readiness check on the Worker itself -- real test upload, R2 object
  // independently re-fetched two different ways (not just trusted the
  // Worker's own response), Drive hand-off confirmed by a clean admin-Chat
  // check (no failure alert). See the PHASE 1 CANDIDATES block below for the
  // fuller history (this route's real two-strike incident record) before
  // touching this line again.
  var JV_HUB_ROUTES = {
    'bridge_party_uploadInspoImage': 'https://jv-inspo-upload.calm-hill-fd5b.workers.dev'
  };

  // Whole-family overrides, matched by prefix, longest first. Use this when
  // a Worker takes over a subsystem rather than a single route, e.g.
  //   'bridge_party_': 'relay',
  var JV_HUB_ROUTE_PREFIXES = {};

  /* ===================================================================
   * PHASE 1 CANDIDATES, shipped DARK (2026-08-07)
   * -------------------------------------------------------------------
   * BRIEFS/BUILD-cloudflare-inspo-upload-2026-08-07.md Phase 1: prove the
   * seam on one real route before trusting it with more.
   *
   * These are NOT applied. They are staged here, OFF, because NO hub route
   * has ever run through the relay in production, and this route is the
   * heaviest request on the hub (a base64 image). Shipping the code and
   * shipping the behaviour are separate decisions, per CLAUDE.md: a
   * "held" comment is not a control, a flag is.
   *
   * CORRECTED 2026-08-08. This entry previously pointed at 'relay', which
   * resolves to the CORS relay, and the CORS relay FORWARDS TO THE SAME GAS
   * /exec. Enabling it therefore routed an upload GAS -> Cloudflare -> GAS:
   * a strictly slower version of today, never the fast path. A live test
   * (BRIEFS/RESULTS-COWORK-arm-phase2a-2026-08-08.md) enabled it, measured
   * 78-85s, saw drive.google.com URLs come back, and correctly concluded the
   * fast path had not been exercised at all. It never could have been.
   *
   * The fast path is the DEDICATED R2 upload Worker on its own host, which
   * is what the original arming commit (7e29b0a0) used and what this entry
   * now names. Verified live 2026-08-08: HTTP 405 on GET in 0.08s, i.e.
   * deployed, healthy, and POST-only as designed.
   *
   * TO ENABLE, in the browser console on the hub:
   *     window.JV_HUB_RELAY_UPLOADS = true;
   * The flag is re-checked on every upload (see resolveUrl below), so this
   * takes effect IMMEDIATELY with NO reload. That matters: a reload destroys
   * the global and the flag is lost, which is the dead end the live test hit
   * when it followed "set it, then reload". Do not reload after setting it.
   * TO ROLL BACK: set it false (immediate), or remove the entry. No deploy.
   * TO ENABLE PERMANENTLY: move the entry into JV_HUB_ROUTES above.
   *
   * Phase 1 changes WHERE the request travels, not what gets written, so
   * it fixes no visible bug on its own. The bug (a photo vanishing on
   * reload) is fixed by Phase 0, a GAS-side metafield write. Do not read a
   * successful Phase 1 as "the upload is fixed".
   * =================================================================== */
  var JV_HUB_PHASE1_RELAY_ROUTES = {
    'bridge_party_uploadInspoImage': 'https://jv-inspo-upload.calm-hill-fd5b.workers.dev'
  };

  /* ===================================================================
   * ADD-ON CATALOG, shipped DARK (2026-08-08)
   * -------------------------------------------------------------------
   * BRIEFS/BUILD-addon-catalog-worker-2026-08-08.md
   *
   * bridge_addon_getCatalog is the hub's ONLY unconditional first-paint
   * GAS read (jv-bridal-hub-widgets.js:342, "On load: probe the catalog").
   * Every other load-read already paints from a metafield island. Routing
   * it to the relay lets the jv-addon-catalog Worker serve it from a D1
   * mirror instead of the Apps Script cold start (~6-14s depending on
   * instance warmth, measured three independent ways 2026-08-08).
   *
   * NOT APPLIED. Staged here, OFF, because shipping the code and shipping
   * the behaviour are separate decisions (CLAUDE.md: a "held" comment is
   * not a control, a flag is).
   *
   * DELIBERATELY SEPARATE from the Phase 1 block above: two independent
   * flags mean either can be rolled back without touching the other. Do
   * not merge them.
   *
   * WHY FLIPPING THIS IS LOW-RISK:
   *   - The relay only diverts the call if BOTH JV_ADDON_CATALOG_URL and
   *     JV_ADDON_CATALOG_KEY are set on it. Unset = inert, straight to GAS.
   *   - The Worker refuses (returns {_addonMiss:true}) whenever a bride is
   *     unmirrored, stale, or it is unsure, and the relay then transparently
   *     re-runs the SAME request against GAS. Worst case is today's speed.
   *   - The call site already treats any non-ok as "leave the pill hidden"
   *     (`if (!json || !json.ok) return;`), which is exactly what a bride
   *     with the feature off sees today. Failure is a hidden secondary
   *     control, not a broken page.
   *
   * TO ENABLE (no deploy needed to turn OFF again):
   *   in the browser console on the hub:
   *     window.JV_HUB_ADDON_CATALOG = true;
   *   Takes effect on the NEXT CALL, with NO reload (see resolveUrl below,
   *   corrected 2026-08-08). Do NOT reload after setting it: a reload destroys
   *   the global and the flag is lost. The earlier "then re-load the page"
   *   instruction was wrong and cost a live test session.
   * TO ENABLE PERMANENTLY: move the entry into JV_HUB_ROUTES above.
   * TO ROLL BACK: set it false, or remove the entry. Instant, no deploy.
   *
   * NOTE: 'relay' is CORRECT for this route. The add-on catalog Worker sits
   * behind the CORS relay, so relay -> Worker is the intended path. That is
   * NOT true of the Phase 1 upload route above, where 'relay' meant
   * relay -> the same GAS. Do not "fix" this one to match that one.
   * =================================================================== */
  var JV_HUB_ADDON_CATALOG_ROUTES = {
    'bridge_addon_getCatalog': 'relay'
  };
  // No parse-time apply block (removed 2026-08-08, same reason as Phase 1
  // above): both dark flags are evaluated PER CALL inside resolveUrl, so a
  // console-set flag applies immediately and survives no reload requirement.

  /* ===================================================================
   * BRIDE RECEIPTS, shipped DARK (2026-08-13)
   * -------------------------------------------------------------------
   * BUILD-bridal-hub-p2-journey-reframe-2026-08-13.md item 3: the "system
   * receipt" pattern. bridge_bride_getReceipts has NO GAS handler and never
   * should get one, this route is designed to read directly from the
   * jv-bride-receipts Cloudflare Worker (D1-backed) through the relay, per the
   * Cloudflare-first hard gate in the build brief. There is nothing to migrate
   * FROM; this is the route's first and only intended backend.
   *
   * NOT YET LIVE. The Worker itself is owned by the Cloudflare / Edge
   * Infrastructure Agent and is not built as of this entry, see
   * BRIEFS/HANDOFF-jv-bride-receipts-worker-to-cloudflare-2026-08-13.md.
   * Staged here, OFF, same discipline as the two dark-flag blocks above: code
   * ships ahead of the backend existing, and flipping it on is a one-line
   * data-entry change once the Worker's real relay route name is known, not a
   * code change.
   *
   * TO ENABLE (once the Worker is live and its relay route is confirmed):
   *   move this entry into JV_HUB_ROUTES above, OR set in the browser console:
   *     window.JV_HUB_BRIDE_RECEIPTS = true;
   *   Takes effect on the next call, no reload, same mechanism as the other
   *   dark flags.
   * TO ROLL BACK: set it false, or remove the entry. No deploy needed.
   * =================================================================== */
  var JV_HUB_BRIDE_RECEIPTS_ROUTES = {
    'bridge_bride_getReceipts': 'relay'
  };

  /* ===================================================================
   * PARTY MEMBER CRUD D1 FAST-PATH, shipped DARK (2026-09-07)
   * -------------------------------------------------------------------
   * BRIEFS/SCOPE-party-member-crud-d1-fast-path-2026-09-05.md +
   * BRIEFS/HANDOFF-party-member-crud-worker-and-trigger-registration-
   * 2026-09-05.md. Three routes (add/edit party members, rename a member,
   * submit a proxy-onboarding form) currently go straight to GAS and pay the
   * full ~8-14s cold-start-plus-write cost every time. The Worker side
   * (jv-bride-saves, POST /party-update /party-rename /party-proxy-
   * onboarding) and the GAS drain side (TriggerOverlord registration
   * e89cf13d, confirmed live 2026-09-07) are BOTH now live. This is the
   * remaining frontend half.
   *
   * WHY NOT A PLAIN JV_HUB_ROUTES ENTRY LIKE THE OTHER MIGRATIONS: the
   * Worker's request shape does not match what jvHubFetch sends verbatim.
   *   - jvHubFetch always POSTs {route, email, customerId, ...payload}. The
   *     Worker instead expects customerEmail (or email -- both accepted)
   *     PLUS a required idempotency_key with no equivalent in the existing
   *     payload, on all three routes.
   *   - bridge_bride_updatePartyMembers's existing call site sends
   *     {partyData: {members: [...]}} (nested); the Worker's /party-update
   *     expects members FLAT on the body. Pointing JV_HUB_ROUTES straight
   *     at the Worker would silently 400 on the missing idempotency_key
   *     with no member data ever reaching D1 -- confirmed by reading the
   *     Worker source directly (DEPOLYMENT/jv-bride-saves/worker.js,
   *     commit fe49da6c), not assumed.
   *   - The Worker also refuses (returns {ok:false, fallback:'gas'}) when
   *     resolveBrideUid/verifyPmIdOwnership can't find the bride/pm_id in
   *     its own D1 identity mirror (e.g. a very new bride/member the mirror
   *     hasn't caught up to yet). That refusal must fall through to the
   *     EXACT SAME direct-GAS call the page already makes today, not a
   *     hard failure -- the fast path can only ever be strictly faster or
   *     no worse than today, never worse.
   *
   * So this is three small adapter functions, not a route-map entry: each
   * builds the Worker-shaped body (adds idempotency_key, flattens
   * updatePartyMembers's payload), posts via jvHub.resolveUrl so it still
   * respects a future permanent JV_HUB_ROUTES migration, and on ANY
   * non-{ok:true} response (including a deliberate fallback:'gas' refusal,
   * a network error, or a timeout) falls straight through to the ORIGINAL
   * gasPost/jvHubFetch-to-GAS call the page already used, unchanged. A
   * caller therefore never has to branch on which path actually served the
   * request -- onOk/onErr fire exactly once either way, same contract as
   * jvHubFetch itself.
   *
   * NOT APPLIED YET. Staged here, OFF, same discipline as the three dark
   * blocks above -- this project has a documented two-strike incident
   * history (bridge_party_uploadInspoImage, see that block's own notes)
   * from arming a write-path Worker migration without a live verification
   * pass first. Do NOT wire these into the call sites in
   * assets/jv-bridal-hub-main.js until each has been armed via the console
   * flag below and confirmed live for a real save (D1 row lands, GAS drain
   * picks it up within ~1 min, _PartyMembers/CRM actually updates) -- not
   * just "the Worker returned ok:true", which only proves the D1 write
   * half.
   *
   * TO ENABLE ONE ROUTE FOR TESTING (arm ONE AT A TIME, per the scope
   * brief's own §7 recommendation -- never all three together):
   *   window.JV_HUB_PARTY_CRUD_FASTPATH = { updatePartyMembers: true };
   *   window.JV_HUB_PARTY_CRUD_FASTPATH = { renamePartyMember: true };
   *   window.JV_HUB_PARTY_CRUD_FASTPATH = { submitProxyOnboarding: true };
   *   window.JV_HUB_PARTY_CRUD_FASTPATH = { generateInviteLink: true };
   * Re-checked per call (same mechanism as the other dark flags), so this
   * takes effect on the very next save, no reload. Setting it to {} or
   * removing the key turns a route back off immediately.
   * TO ENABLE PERMANENTLY: once a route is live-verified, wire its adapter
   * into the matching call site in assets/jv-bridal-hub-main.js in place of
   * the current window.jvHubFetch/gasPost call, and remove that route's
   * flag check below (the adapter itself, including its GAS fallback,
   * stays -- only the "is this route even on" gate goes away).
   * =================================================================== */
  var JV_HUB_PARTY_CRUD_WORKER = 'https://jv-bride-saves.calm-hill-fd5b.workers.dev';

  function jvHubPartyCrud_idempotencyKey_(brideUidPart) {
    return String(brideUidPart || 'x') + '|' + Date.now() + '|' + Math.random().toString(36).slice(2);
  }

  /**
   * jvHubPartyCrud.updatePartyMembers(members, opts)
   *   opts: { email, customerId, brideName, timeoutMs, onOk, onErr }
   * Flattens members onto the Worker body (the existing GAS route nests it
   * under partyData.members; the Worker does not). Falls back to the exact
   * existing GAS call (bridge_bride_updatePartyMembers via jvHubFetch) on
   * any non-ok response, including a deliberate {fallback:'gas'} refusal.
   */
  window.jvHubPartyCrud = window.jvHubPartyCrud || {};
  window.jvHubPartyCrud.updatePartyMembers = function (members, opts) {
    opts = opts || {};
    var ctx = window.JV_HUB_CTX || {};
    var email = (opts.email != null) ? opts.email : ctx.email;
    var customerId = (opts.customerId != null) ? opts.customerId : ctx.customerId;

    function toGas() {
      window.jvHubFetch('bridge_bride_updatePartyMembers', { partyData: { members: members } }, opts);
    }

    if (!(window.JV_HUB_PARTY_CRUD_FASTPATH && window.JV_HUB_PARTY_CRUD_FASTPATH.updatePartyMembers)) {
      toGas();
      return;
    }

    var url = window.jvHub.resolveUrl('__jvHubPartyCrudUpdate', JV_HUB_PARTY_CRUD_WORKER + '/party-update');
    var body = {
      customerEmail: email, customerId: customerId, brideName: opts.brideName || '',
      members: members, idempotency_key: jvHubPartyCrud_idempotencyKey_(customerId)
    };
    var f = (window._jvNativeFetch || window.fetch).bind(window);
    f(url, { method: 'POST', mode: 'cors', credentials: 'omit', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify(body) })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('http_' + r.status)); })
      .then(function (text) {
        var d = null;
        try { d = JSON.parse(text); } catch (e) {}
        if (d && d.ok) { if (opts.onOk) opts.onOk(d); return; }
        toGas(); // deliberate refusal (fallback:'gas') or malformed response -- try the real path
      })
      .catch(toGas); // network/timeout on the fast path -- the bride must never see this, GAS is the safety net
  };

  /**
   * jvHubPartyCrud.renamePartyMember(pmId, newName, opts)
   * Same fallback contract as updatePartyMembers above.
   */
  window.jvHubPartyCrud.renamePartyMember = function (pmId, newName, opts) {
    opts = opts || {};
    var ctx = window.JV_HUB_CTX || {};
    var email = (opts.email != null) ? opts.email : ctx.email;
    var customerId = (opts.customerId != null) ? opts.customerId : ctx.customerId;

    function toGas() {
      window.jvHubFetch('bridge_bride_renamePartyMember', { pm_id: pmId, newName: newName }, opts);
    }

    if (!(window.JV_HUB_PARTY_CRUD_FASTPATH && window.JV_HUB_PARTY_CRUD_FASTPATH.renamePartyMember)) {
      toGas();
      return;
    }

    var url = window.jvHub.resolveUrl('__jvHubPartyCrudRename', JV_HUB_PARTY_CRUD_WORKER + '/party-rename');
    var body = {
      customerEmail: email, customerId: customerId, brideName: opts.brideName || '',
      pm_id: pmId, newName: newName, idempotency_key: jvHubPartyCrud_idempotencyKey_(pmId)
    };
    var f = (window._jvNativeFetch || window.fetch).bind(window);
    f(url, { method: 'POST', mode: 'cors', credentials: 'omit', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify(body) })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('http_' + r.status)); })
      .then(function (text) {
        var d = null;
        try { d = JSON.parse(text); } catch (e) {}
        if (d && d.ok) { if (opts.onOk) opts.onOk(d); return; }
        toGas();
      })
      .catch(toGas);
  };

  /**
   * jvHubPartyCrud.submitProxyOnboarding(pmId, memberIndex, formData, opts)
   * Same fallback contract as updatePartyMembers above.
   */
  window.jvHubPartyCrud.submitProxyOnboarding = function (pmId, memberIndex, formData, opts) {
    opts = opts || {};
    var ctx = window.JV_HUB_CTX || {};
    var email = (opts.email != null) ? opts.email : ctx.email;
    var customerId = (opts.customerId != null) ? opts.customerId : ctx.customerId;

    function toGas() {
      window.jvHubFetch('bridge_bride_submitProxyOnboarding', { memberIndex: memberIndex, pmId: pmId, formData: formData }, opts);
    }

    if (!(window.JV_HUB_PARTY_CRUD_FASTPATH && window.JV_HUB_PARTY_CRUD_FASTPATH.submitProxyOnboarding) || !pmId) {
      // No pm_id (legacy memberIndex-only add flow): the fast path REQUIRES
      // pm_id (per the scope brief's ordering-hazard note), so an empty
      // pm_id always goes straight to GAS, flag or no flag.
      toGas();
      return;
    }

    var url = window.jvHub.resolveUrl('__jvHubPartyCrudProxyOnboarding', JV_HUB_PARTY_CRUD_WORKER + '/party-proxy-onboarding');
    var body = {
      customerEmail: email, customerId: customerId, brideName: opts.brideName || '',
      pm_id: pmId, formData: formData, idempotency_key: jvHubPartyCrud_idempotencyKey_(pmId)
    };
    var f = (window._jvNativeFetch || window.fetch).bind(window);
    f(url, { method: 'POST', mode: 'cors', credentials: 'omit', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify(body) })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('http_' + r.status)); })
      .then(function (text) {
        var d = null;
        try { d = JSON.parse(text); } catch (e) {}
        if (d && d.ok) { if (opts.onOk) opts.onOk(d); return; }
        toGas();
      })
      .catch(toGas);
  };

  /* ===================================================================
   * PARTY INVITE-LINK D1 FAST-PATH, shipped DARK, 2026-10-27
   * -------------------------------------------------------------------
   * Issue #11, se-party-invite-link-email-fallback-2026-10-27, extended
   * se-party-invite-link-bride-relay-2026-10-27. Mirrors
   * window.jvHubFetch('bridge_party_generateInviteLink', ...) (the current
   * real call site, assets/jv-bridal-hub-main.js ~line 1678) against the
   * CF-native POST /party-invite-link route on jv-crm-mirror
   * (handlePartyInviteLink, booking-engine-phase1.mjs).
   *
   * AUTH PATH (resolved se-party-invite-link-bride-relay-2026-10-27): the
   * EARLIER comment here said no safe bride-facing path to this route
   * existed -- jv-crm-mirror's requireSecret(body.secret, env) against
   * env.D1_SYNC_SECRET is a real backend-only secret this adapter must
   * never embed. That gap is now closed the SAME way bride-trial-update.mjs
   * closed it: a Shopify App Proxy relay entity
   * (cloudflare-cors-relay/proxy-entities-party-invite-link.mjs,
   * actorType:'bride') that verifies the signed request, resolves the
   * logged-in customer's REAL email server-side, and injects
   * verifiedActorEmail/verifiedActorType:'bride'/the shared secret into the
   * downstream body itself -- the browser never holds the secret or
   * chooses the identity. handlePartyInviteLink's bride-relay identity tier
   * uses verifiedActorEmail as the ONLY source of truth whenever it is
   * present, ignoring any brideUid/email the body also carries, so this
   * adapter sends neither.
   *
   * STILL DARK ON BOTH SIDES, independently:
   *   - jv-crm-mirror: PARTY_INVITE_LINK_BRIDE_CF_ENABLED (default unset)
   *     + PARTY_INVITE_LINK_BRIDE_SCOPE_EMAILS (default empty = nobody).
   *   - this adapter: window.JV_HUB_PARTY_CRUD_FASTPATH.generateInviteLink
   *     (default unset -> always GAS, below).
   * All three must be true for a real fast-path call to ever reach D1; any
   * one of them being off/false means every real call still goes to GAS,
   * unchanged, exactly as before this file existed.
   *
   * NOT APPLIED YET. Do NOT wire this into the real call site in
   * assets/jv-bridal-hub-main.js until the two Worker-side flags above have
   * been armed and confirmed live for a real mint (D1 row lands with the
   * right bride_id, GAS fallback never silently double-mints).
   *
   * TO ENABLE FOR TESTING (once the two Worker-side flags are armed):
   *   window.JV_HUB_PARTY_CRUD_FASTPATH = { generateInviteLink: true };
   * Re-checked per call, same mechanism as the other dark flags above.
   * =================================================================== */

  /**
   * jvHubPartyCrud.generateInviteLink(memberName, memberEmail, memberRole, opts)
   *   opts: { customerId, brideName, timeoutMs, onOk, onErr }
   * Same fallback contract as the other jvHubPartyCrud adapters above: any
   * non-{ok:true} response (including a deliberate refusal from the
   * Worker-side flags above, a network error, or a timeout) falls straight
   * through to the EXACT SAME bridge_party_generateInviteLink GAS call the
   * real call site already uses, unchanged.
   */
  window.jvHubPartyCrud.generateInviteLink = function (memberName, memberEmail, memberRole, opts) {
    opts = opts || {};

    function toGas() {
      window.jvHubFetch(
        'bridge_party_generateInviteLink',
        { memberName: memberName, memberEmail: memberEmail || '', memberRole: memberRole || 'Party Member' },
        opts
      );
    }

    if (!(window.JV_HUB_PARTY_CRUD_FASTPATH && window.JV_HUB_PARTY_CRUD_FASTPATH.generateInviteLink)) {
      toGas();
      return;
    }

    // Same-origin Shopify App Proxy base every other relay-entity caller on
    // this storefront falls back to when no explicit config is set
    // (assets/jv-team-profile.js, assets/jv-runsheet-view.js, etc.) --
    // Shopify signs the request en route to the relay, which is what lets
    // the relay verify it and resolve the real logged-in customer.
    var proxyBase = (window.jvBridge && window.jvBridge.appProxyBase) || '/apps/jv';
    var url = window.jvHub.resolveUrl(
      '__jvHubPartyInviteLink',
      String(proxyBase).replace(/\/+$/, '') + '/partner/party-invite-link/generate'
    );
    var body = {
      // No identity key here on purpose -- the relay injects
      // verifiedActorEmail/verifiedActorType server-side from the signed
      // request. Sending brideUid/email from the browser would be ignored
      // by handlePartyInviteLink's bride-relay tier anyway (see this
      // block's own header comment), so it is left out rather than sent
      // and silently discarded.
      memberName: memberName, memberEmail: memberEmail || '', memberRole: memberRole || 'Party Member'
    };
    var f = (window._jvNativeFetch || window.fetch).bind(window);
    f(url, { method: 'POST', mode: 'cors', credentials: 'omit', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify(body) })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('http_' + r.status)); })
      .then(function (text) {
        var d = null;
        try { d = JSON.parse(text); } catch (e) {}
        if (d && d.ok) { if (opts.onOk) opts.onOk(d); return; }
        toGas(); // deliberate refusal (flag off / out_of_scope / fallback:'gas') or malformed response -- try the real path
      })
      .catch(toGas); // network/timeout on the fast path -- the bride must never see this, GAS is the safety net
  };

  window.jvHub.BACKENDS = JV_HUB_BACKENDS;
  window.jvHub.ROUTES = JV_HUB_ROUTES;
  window.jvHub.ROUTE_PREFIXES = JV_HUB_ROUTE_PREFIXES;
  window.jvHub.DEFAULT_BACKEND = DEFAULT_BACKEND;
  window.jvHub.PHASE1_RELAY_ROUTES = JV_HUB_PHASE1_RELAY_ROUTES;

  /**
   * jvHub.resolveUrl(route, explicitUrl)
   * Returns the absolute endpoint a given route should post to.
   * explicitUrl (a caller's opts.url) always wins, so an existing call site
   * that passes a URL keeps working byte-for-byte while it is migrated.
   * Never returns undefined.
   */
  window.jvHub.resolveUrl = function (route, explicitUrl) {
    if (explicitUrl) return explicitUrl;

    var target = null;

    // DARK-FLAG OVERRIDES, evaluated PER CALL rather than once at parse time.
    //
    // Why per call: these flags are set by hand in the browser console during a
    // live test. The previous design applied them once, while this file was
    // parsed, so the only way to use one was to set it BEFORE the script ran,
    // which a console cannot do. Following the old instructions ("set it, then
    // reload") destroyed the global on every reload and the flag never applied
    // (BRIEFS/RESULTS-COWORK-arm-phase2a-2026-08-08.md). Checking here means
    // setting the flag takes effect on the very next upload, with no reload,
    // and setting it false reverts just as immediately.
    //
    // A permanent migration still belongs in JV_HUB_ROUTES above, which is
    // checked first below and always wins. This block is for TESTING only.
    // A flag's value is a BACKENDS key ('relay') or a literal URL, exactly like
    // a JV_HUB_ROUTES value, so it must flow through the SAME resolution below
    // rather than being returned raw. Returning 'relay' verbatim would POST to
    // a relative path and fail silently, which is worse than the problem this
    // block solves. Caught by the behavioural test before this shipped.
    // A real JV_HUB_ROUTES entry always wins, so a permanent migration is never
    // overridden by a stale console flag.
    if (route && !Object.prototype.hasOwnProperty.call(JV_HUB_ROUTES, route)) {
      if (window.JV_HUB_RELAY_UPLOADS === true &&
          Object.prototype.hasOwnProperty.call(JV_HUB_PHASE1_RELAY_ROUTES, route)) {
        target = JV_HUB_PHASE1_RELAY_ROUTES[route];
      } else if (window.JV_HUB_ADDON_CATALOG === true &&
          Object.prototype.hasOwnProperty.call(JV_HUB_ADDON_CATALOG_ROUTES, route)) {
        target = JV_HUB_ADDON_CATALOG_ROUTES[route];
      } else if (window.JV_HUB_BRIDE_RECEIPTS === true &&
          Object.prototype.hasOwnProperty.call(JV_HUB_BRIDE_RECEIPTS_ROUTES, route)) {
        target = JV_HUB_BRIDE_RECEIPTS_ROUTES[route];
      }
    }

    if (target) {
      // already chosen by a dark flag above
    } else if (route && Object.prototype.hasOwnProperty.call(JV_HUB_ROUTES, route)) {
      target = JV_HUB_ROUTES[route];
    } else if (route) {
      // Longest prefix wins, so a specific family beats a broad one.
      var best = '';
      Object.keys(JV_HUB_ROUTE_PREFIXES).forEach(function (p) {
        if (route.indexOf(p) === 0 && p.length > best.length) best = p;
      });
      if (best) target = JV_HUB_ROUTE_PREFIXES[best];
    }

    if (!target) target = DEFAULT_BACKEND;

    // A key into BACKENDS, or a literal URL, or (on a typo) the default.
    if (Object.prototype.hasOwnProperty.call(JV_HUB_BACKENDS, target)) {
      return JV_HUB_BACKENDS[target];
    }
    if (typeof target === 'string' && target.indexOf('http') === 0) {
      return target;
    }
    return JV_HUB_BACKENDS[DEFAULT_BACKEND];
  };

  // Warm, never-technical copy. No stack traces, no HTTP codes, no emoji.
  window.jvHub.DEFAULT_ERROR_MSG =
    "That didn't save. Your connection may have dropped. Tap to try again.";

  // Hard ceiling for any caller that does not pass its own timeoutMs.
  // RAISED 2026-08-27 (Grade-3 XRAY audit F1, jv-bridal-hub-xray-fixes brief):
  // the original 15000ms estimate of "8-14s real GAS write" was wrong in
  // practice. At least 6 separate dated incidents (spotlight saves, party
  // rename, invite-link generation, service-detail load, inspo-image load,
  // photo upload) each hit the SAME false-failure bug, a write that actually
  // succeeded on a cold GAS instance but was reported as failed because it
  // took longer than this default. Every one was patched individually with
  // an explicit { timeoutMs: 45000 } at its own call site rather than fixed
  // here, so any NEW call site added without knowing this history reproduces
  // the same bug on day one. Live-timed 2026-08-27: a single-field profile
  // save took ~18s, invite-link generation ~20s, both against a normal cold
  // instance, not an outlier. Matching the value every write call site had
  // already independently converged on removes the whack-a-mole pattern at
  // the root: a caller that forgets to override now gets a safe default
  // instead of a false failure. Fast reads are unaffected either way, they
  // resolve in ~200ms-2s and were never at risk from this ceiling.
  window.jvHub.TIMEOUT_MS = 45000;

  /**
   * jvHubFetch(route, payload, opts)
   *  opts: {
   *    url, email, customerId,   // fall back to window.JV_HUB_CTX, then defaults
   *    onOk(data), onErr(info),  // info = { reason: 'timeout' | 'network' }
   *    timeoutMs
   *  }
   * onOk / onErr fire exactly once. onErr fires on timeout AND on transport
   * failure, so callers never need their own timer.
   */
  window.jvHubFetch = function (route, payload, opts) {
    opts = opts || {};
    var ctx = window.JV_HUB_CTX || {};
    // Resolve through the route map. An explicit opts.url (or ctx.url) still
    // wins, so existing call sites that pass a URL are unaffected while they
    // migrate. See the ROUTE -> BACKEND MAP block above.
    var url = window.jvHub.resolveUrl(route, opts.url || ctx.url);
    var email = (opts.email != null) ? opts.email : ctx.email;
    var customerId = (opts.customerId != null) ? opts.customerId : ctx.customerId;

    var body = { route: route, email: email, customerId: customerId };

    // Wedding date is the SECOND FACTOR on the GAS customerId-mismatch auth
    // branch (clientApi_brideShopifyAuth_, hardened 2026-08-04). Without it,
    // that branch resolves by email alone. Attach it from context here, at the
    // single shared transport, so every hub caller carries it without each
    // call site having to remember. That is the same reason email and
    // customerId are defaulted above. An explicit payload.weddingDate wins.
    var weddingDate = (opts.weddingDate != null) ? opts.weddingDate : ctx.weddingDate;
    if (weddingDate) body.weddingDate = weddingDate;

    if (payload && typeof payload === 'object') {
      Object.keys(payload).forEach(function (k) { body[k] = payload[k]; });
    }

    var f = (window._jvNativeFetch || window.fetch).bind(window);
    var ctrl = (typeof AbortController !== 'undefined') ? new AbortController() : null;
    var settled = false;
    var ms = opts.timeoutMs || window.jvHub.TIMEOUT_MS;

    var timer = setTimeout(function () {
      if (settled) return;
      settled = true;
      if (ctrl) { try { ctrl.abort(); } catch (e) {} }
      if (opts.onErr) opts.onErr({ reason: 'timeout' });
    }, ms);

    var fetchOpts = {
      method: 'POST',
      mode: 'cors',
      credentials: 'omit',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify(body)
    };
    if (ctrl) fetchOpts.signal = ctrl.signal;

    f(url, fetchOpts)
      .then(function (r) {
        // BUG FIX (2026-08-27, XRAY audit P0): fetch() only rejects on a real
        // network failure. An HTTP error status (503, 500, etc) resolves
        // successfully here just like a 200 does, so a live audit pass saw
        // GAS 503s during a page load produce zero console error and zero
        // caller-visible failure, whatever that call was supposed to load
        // simply did not appear. r.text() below would parse a 503's plain-
        // text/HTML error body, fail JSON.parse silently, and hand every
        // caller onOk(null) as if the call had SUCCEEDED with an empty
        // result. Checking r.ok here routes a non-2xx into the SAME onErr
        // path as a timeout or a network drop, so callers already built to
        // handle onErr (retry UI, warm error copy) now also catch this.
        if (!r.ok) {
          var err = new Error('http_' + r.status);
          err.jvHttpStatus = r.status;
          throw err;
        }
        return r.text();
      })
      .then(function (text) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        var d = null;
        try { d = JSON.parse(text); } catch (e) {}
        // BUG FIX (2026-09-10, party-CRUD D1 re-verify §6): an HTTP 200 with an
        // EXPLICIT {success:false,...} body (a GAS route that flatly rejected the
        // write, e.g. "Maximum 20 party members allowed") was routed to onOk
        // because this function only ever checked r.ok (HTTP status), never the
        // response body's own success field. A hard failure was announced to the
        // bride as a save that worked. STRICT check (=== false, not falsy/absent)
        // so a response shape that doesn't use `success` at all (the majority of
        // this transport's 14 call sites) is completely unaffected -- this only
        // fires onErr when the body EXPLICITLY says failure, never on an
        // undefined/missing field being mistaken for one.
        if (d && d.success === false) {
          if (opts.onErr) opts.onErr({ reason: 'success_false', body: d });
          return;
        }
        if (opts.onOk) opts.onOk(d);
      })
      .catch(function (e) {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        var info = { reason: 'network' };
        if (e && e.jvHttpStatus) { info.reason = 'http'; info.status = e.jvHttpStatus; }
        if (opts.onErr) opts.onErr(info);
      });
  };

  /**
   * jvHubWarmError(el, retryFn, opts)
   * Renders a soft inline error into `el` with a brand-styled retry affordance.
   *  opts: { message, retryLabel, baseClass }
   * Clicking retry clears the message and calls retryFn(). Safe to call with a
   * null el (no-op). Generalises the avatar-upload error copy that used to live
   * inline at snippets/jv-bride-spotlight.liquid.
   */
  window.jvHubWarmError = function (el, retryFn, opts) {
    if (!el) return;
    opts = opts || {};
    var msg = opts.message || window.jvHub.DEFAULT_ERROR_MSG;

    el.className = (opts.baseClass ? opts.baseClass + ' ' : '') + 'jv-hub-warn';
    el.innerHTML = '';

    var span = document.createElement('span');
    span.className = 'jv-hub-warn__text';
    span.textContent = msg;
    el.appendChild(span);

    if (typeof retryFn === 'function') {
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'jv-hub-warn__retry';
      btn.textContent = opts.retryLabel || 'Try again';
      btn.addEventListener('click', function () {
        el.className = opts.baseClass || '';
        el.innerHTML = '';
        retryFn();
      });
      el.appendChild(btn);
    }
  };

  // Convenience: clear a warm-error element back to empty.
  window.jvHubClear = function (el, baseClass) {
    if (!el) return;
    el.className = baseClass || '';
    el.innerHTML = '';
    el.textContent = '';
  };

  /* ===================================================================
   * PORTAL PREFS D1-FREE FAST-PATH, shipped DARK (2026-09-30)
   * -------------------------------------------------------------------
   * state/findings/SCOPE-portal-prefs-cloudflare-replacement-2026-09-30.md.
   * Replaces bridge_bride_updatePortalPrefs (the "snooze/disable these
   * tips" buttons) with a direct call to the jv-portal-prefs Worker, a
   * standalone Cloudflare Worker holding its own scoped Shopify Admin
   * credential. Root-traced: this route never touches the CRM sheet or
   * D1 at all, it is a pure Shopify-metafield read-merge-write, so unlike
   * the party-CRUD migration there is no write-outbox/GAS-drain needed,
   * once trusted, GAS is fully removable from this path, not just
   * fronted.
   *
   * RESPONSE SHAPE, NO TRANSLATION NEEDED, unlike party-CRUD. Both the
   * Worker's success response ({ok:true, action, writtenTo:'shopify'})
   * and GAS's own response ({ok:true, action}) key off the SAME `ok`
   * field the existing callers already check, so the Worker's response
   * can be handed straight to the same onOk callback whichever path
   * served it, no shape-mismatch guard required here.
   *
   * NOT APPLIED YET. Ships OFF, same two-strike-incident discipline as
   * every other Worker migration tonight: enable via the console flag
   * below, verify a REAL snooze/disable lands on the correct customer's
   * custom.portal_prompt_log metafield with the CORRECT identity (not
   * silently written to the wrong customer), before wiring this
   * permanently into assets/jv-bridal-hub-team.js in place of the
   * current gasPost('bridge_bride_updatePortalPrefs', ...) calls.
   *
   * TO ENABLE FOR TESTING (one action at a time):
   *   window.JV_HUB_PORTAL_PREFS_FASTPATH = { snooze: true };
   *   window.JV_HUB_PORTAL_PREFS_FASTPATH = { disable: true };
   *   window.JV_HUB_PORTAL_PREFS_FASTPATH = { enable: true };
   * Re-checked per call, takes effect on the very next click, no reload.
   * ===================================================================== */
  var JV_HUB_PORTAL_PREFS_WORKER = 'https://jv-portal-prefs.calm-hill-fd5b.workers.dev';

  /**
   * jvHubPortalPrefs.update(action, opts)
   *   action: 'snooze' | 'disable' | 'enable'
   *   opts: { email, customerId, onOk, onErr }
   * Falls back to the exact existing GAS call
   * (bridge_bride_updatePortalPrefs via jvHubFetch) on any non-ok
   * response, including a deliberate {fallback:'gas'} refusal, a network
   * error, or the flag simply being off. A caller never has to branch on
   * which path actually served the request.
   */
  window.jvHubPortalPrefs = window.jvHubPortalPrefs || {};
  window.jvHubPortalPrefs.update = function (action, opts) {
    opts = opts || {};
    var ctx = window.JV_HUB_CTX || {};
    var email = (opts.email != null) ? opts.email : ctx.email;
    var customerId = (opts.customerId != null) ? opts.customerId : ctx.customerId;

    function toGas() {
      window.jvHubFetch('bridge_bride_updatePortalPrefs', { action: action }, opts);
    }

    if (!(window.JV_HUB_PORTAL_PREFS_FASTPATH && window.JV_HUB_PORTAL_PREFS_FASTPATH[action])) {
      toGas();
      return;
    }

    var url = window.jvHub.resolveUrl('__jvHubPortalPrefsUpdate', JV_HUB_PORTAL_PREFS_WORKER + '/update');
    var body = { customerEmail: email, customerId: customerId, action: action };
    var f = (window._jvNativeFetch || window.fetch).bind(window);
    f(url, { method: 'POST', mode: 'cors', credentials: 'omit', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify(body) })
      .then(function (r) { return r.ok ? r.text() : Promise.reject(new Error('http_' + r.status)); })
      .then(function (text) {
        var d = null;
        try { d = JSON.parse(text); } catch (e) {}
        if (d && d.ok) { if (opts.onOk) opts.onOk(d); return; }
        toGas(); // deliberate refusal (identity mismatch, customer not found, fallback:'gas') or malformed response
      })
      .catch(toGas); // network/timeout on the fast path, the bride must never see this, GAS is the safety net
  };
})();
